import type { SchoolSummary } from '@ucp/web/lib';
import { cn } from './ui/cn';
import { Select } from './ui/Select';

/**
 * Active-school switcher (PRO-0030 §5). Lets a multi-school L1/L2 caller pick the
 * school their requests act as; the choice drives the `x-ucp-school-id` header.
 * Visible only when the caller has more than one school (DS-5). Accessible: a
 * labelled control, ≥44px target, visible focus (NF-10 / WCAG 2.2 AA).
 *
 * Tokenised under PRO-0260 (ADR-015) — token classes for the select/label; the
 * inline `minHeight: 44` is retained intentionally as the canonical 44px target
 * the shell a11y regression test asserts.
 *
 * Options are listed A–Z by name (case-insensitive, natural numbers). Callers
 * pass assignment-id order; the selected school is unchanged.
 */
export interface SchoolSwitcherProps {
  schools: readonly SchoolSummary[];
  activeSchoolId: string;
  onChange: (schoolId: string) => void;
  /** When true, use compact mobile styling (visually hidden label, capped width). */
  compact?: boolean;
  className?: string;
}

/** Display order only. Does not change which school is selected. */
function compareSchoolOption(a: SchoolSummary, b: SchoolSummary): number {
  const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  if (byName !== 0) {
    return byName;
  }
  return a.schoolId.localeCompare(b.schoolId);
}

export function SchoolSwitcher({
  schools,
  activeSchoolId,
  onChange,
  compact = false,
  className,
}: SchoolSwitcherProps) {
  if (schools.length <= 1) {
    return null;
  }

  const ordered = [...schools].sort(compareSchoolOption);

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <label
        htmlFor="ucp-school-switcher"
        className={cn('text-sm text-text-secondary', compact && 'max-md:sr-only')}
      >
        Active school or service
      </label>
      <Select
        id="ucp-school-switcher"
        value={activeSchoolId}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          compact && 'max-w-[7.5rem] min-w-0 truncate text-sm md:max-w-none md:text-base',
        )}
        style={{ minHeight: 44 }}
      >
        {ordered.map((school) => (
          <option key={school.schoolId} value={school.schoolId}>
            {school.name}
          </option>
        ))}
      </Select>
    </div>
  );
}
